// src/pages/SignupPage.js - Modern signup page matching landing page design
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { authService } from '../services/api';
import GoogleSignInButton from '../components/GoogleSignInButton';
import logo from '../assets/mandap-logo.jpeg';

// ============================================
// DESIGN TOKENS (matching LandingPage)
// ============================================
const tokens = {
  colors: {
    primary: '#1e514b',
    primaryLight: '#2a6b63',
    cream: '#faf8f5',
    warmWhite: '#fffdf9',
    charcoal: '#1a1a1a',
    slate: '#4a4a4a',
    muted: '#8a8a8a',
    border: '#e8e4de',
    error: '#dc3545',
    errorBg: '#fdf2f2',
  },
  fonts: {
    display: '"Playfair Display", Georgia, serif',
    body: '"DM Sans", -apple-system, sans-serif',
  },
};

// ============================================
// STYLED COMPONENTS
// ============================================
const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100vw;
  display: flex;
  background: ${tokens.colors.warmWhite};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;

  @media (max-width: 968px) {
    flex-direction: column;
    position: relative;
    width: 100%;
  }
`;

const BrandPanel = styled.div`
  flex: 1;
  background: linear-gradient(135deg, ${tokens.colors.primary} 0%, ${tokens.colors.primaryLight} 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }

  @media (max-width: 968px) {
    padding: 2rem;
    min-height: auto;
  }
`;

const BrandContent = styled(motion.div)`
  position: relative;
  z-index: 1;
  text-align: center;
  color: white;
  max-width: 400px;
`;

const LogoWrapper = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  text-decoration: none;
  margin-bottom: 2rem;

  img {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }

  span {
    font-family: ${tokens.fonts.display};
    font-size: 2rem;
    font-weight: 600;
    color: white;
  }
`;

const BrandHeadline = styled.h1`
  font-family: ${tokens.fonts.display};
  font-size: 2.5rem;
  font-weight: 500;
  line-height: 1.2;
  margin-bottom: 1rem;

  em {
    font-style: italic;
    opacity: 0.9;
  }

  @media (max-width: 968px) {
    font-size: 1.75rem;
  }
`;

const BrandSubtext = styled.p`
  font-family: ${tokens.fonts.body};
  font-size: 1.1rem;
  opacity: 0.85;
  line-height: 1.6;

  @media (max-width: 968px) {
    font-size: 1rem;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0 0;
  text-align: left;

  @media (max-width: 968px) {
    display: none;
  }
`;

const FeatureItem = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: ${tokens.fonts.body};
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  opacity: 0.9;

  span {
    font-size: 1.1rem;
  }
`;

const FormPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  background: ${tokens.colors.warmWhite};

  @media (max-width: 968px) {
    padding: 2rem 1.5rem;
  }
`;

const FormContainer = styled(motion.div)`
  width: 100%;
  max-width: 420px;
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;

  h2 {
    font-family: ${tokens.fonts.display};
    font-size: 1.75rem;
    font-weight: 500;
    color: ${tokens.colors.charcoal};
    margin-bottom: 0.5rem;
  }

  p {
    font-family: ${tokens.fonts.body};
    font-size: 0.95rem;
    color: ${tokens.colors.muted};
  }
`;

const GoogleSection = styled.div`
  margin-bottom: 1.5rem;

  /* Override Google button styles for better integration */
  > div {
    display: flex;
    justify-content: center;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${tokens.colors.border};
  }

  span {
    padding: 0 1rem;
    font-family: ${tokens.fonts.body};
    font-size: 0.85rem;
    color: ${tokens.colors.muted};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-family: ${tokens.fonts.body};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${tokens.colors.charcoal};
`;

const Input = styled.input`
  font-family: ${tokens.fonts.body};
  font-size: 1rem;
  padding: 0.875rem 1rem;
  border: 1.5px solid ${tokens.colors.border};
  border-radius: 10px;
  background: white;
  color: ${tokens.colors.charcoal};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${tokens.colors.primary};
    box-shadow: 0 0 0 3px rgba(30, 81, 75, 0.1);
  }

  &:disabled {
    background: ${tokens.colors.cream};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${tokens.colors.muted};
  }
`;

const PasswordRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled(motion.div)`
  background: ${tokens.colors.errorBg};
  border: 1px solid ${tokens.colors.error};
  color: ${tokens.colors.error};
  padding: 0.875rem 1rem;
  border-radius: 10px;
  font-family: ${tokens.fonts.body};
  font-size: 0.9rem;
`;

const SubmitButton = styled(motion.button)`
  font-family: ${tokens.fonts.body};
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem;
  border: none;
  border-radius: 10px;
  background: ${tokens.colors.primary};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    background: ${tokens.colors.primaryLight};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const FormFooter = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-family: ${tokens.fonts.body};
  font-size: 0.95rem;
  color: ${tokens.colors.slate};

  a {
    color: ${tokens.colors.primary};
    text-decoration: none;
    font-weight: 500;
    margin-left: 0.25rem;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const BackToHome = styled(Link)`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${tokens.fonts.body};
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.2s ease;
  z-index: 10;

  &:hover {
    color: white;
  }

  @media (max-width: 968px) {
    color: ${tokens.colors.slate};
    position: relative;
    top: auto;
    left: auto;
    margin-bottom: 1rem;

    &:hover {
      color: ${tokens.colors.primary};
    }
  }
`;

// ============================================
// COMPONENT
// ============================================
const WaitlistConfirmation = styled(motion.div)`
  text-align: center;
  padding: 2rem 0;

  h2 {
    font-family: ${tokens.fonts.display};
    font-size: 1.75rem;
    font-weight: 500;
    color: ${tokens.colors.charcoal};
    margin-bottom: 1rem;
  }

  p {
    font-family: ${tokens.fonts.body};
    font-size: 1rem;
    color: ${tokens.colors.slate};
    line-height: 1.6;
    margin-bottom: 0.75rem;
  }
`;

const WaitlistBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${tokens.colors.cream};
  border: 1.5px solid ${tokens.colors.border};
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin: 1.5rem 0;
  font-family: ${tokens.fonts.body};
  font-size: 0.95rem;
  color: ${tokens.colors.charcoal};

  strong {
    font-size: 1.5rem;
    color: ${tokens.colors.primary};
  }
`;

const SignupPage = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [waitlistInfo, setWaitlistInfo] = useState(null);
  const navigate = useNavigate();

  // Pre-fill email from invitation if provided
  useEffect(() => {
    if (location.state?.email) {
      setFormData(prev => ({
        ...prev,
        email: location.state.email
      }));
    }
  }, [location.state]);

  const handleGoogleSuccess = (data) => {
    const returnTo = location.state?.returnTo || '/events';
    navigate(returnTo);
  };

  const handleGoogleError = (errorMessage, errorData) => {
    if (errorData?.code === 'ACCOUNT_PENDING') {
      setWaitlistInfo({ position: errorData.waitlistPosition, isNewUser: errorData.isNewUser });
      setError('');
    } else {
      setError(errorMessage);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.register({
        name: formData.name,
        email: formData.email,
        password: formData.password
      });

      if (response.waitlisted) {
        setWaitlistInfo({ position: response.waitlistPosition });
        return;
      }

      // Fallback for non-gated flow (shouldn't happen in production)
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }
      const returnTo = location.state?.returnTo || '/events';
      navigate(returnTo);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Signup failed. Please try again.';
      if (errorMessage === 'User already exists') {
        setError(`An account with ${formData.email} already exists. Please log in instead.`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: '✨', text: 'Set up your first event in 5 minutes' },
    { icon: '💬', text: 'AI-powered WhatsApp guest chat' },
    { icon: '📊', text: 'Real-time RSVP tracking' },
    { icon: '🎯', text: 'Smart automation for reminders' },
  ];

  return (
    <PageWrapper>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <BrandPanel>
        <BackToHome to="/">
          ← Back to home
        </BackToHome>
        <BrandContent
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <LogoWrapper to="/">
            <img src={logo} alt="Mandap" />
            <span>mandap</span>
          </LogoWrapper>
          <BrandHeadline>
            Plan weddings,<br />not <em>spreadsheets.</em>
          </BrandHeadline>
          <BrandSubtext>
            Join thousands of planners who've simplified their workflow with AI-powered automation.
          </BrandSubtext>
          <FeatureList>
            {features.map((feature, index) => (
              <FeatureItem
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
              >
                <span>{feature.icon}</span>
                {feature.text}
              </FeatureItem>
            ))}
          </FeatureList>
        </BrandContent>
      </BrandPanel>

      <FormPanel>
        <FormContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {waitlistInfo ? (
            <WaitlistConfirmation
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <h2>You're on the list!</h2>
              <p>Thanks for signing up for Mandap. We're onboarding planners in batches to ensure the best experience.</p>
              {waitlistInfo.position && (
                <WaitlistBadge>
                  Your position: <strong>#{waitlistInfo.position}</strong>
                </WaitlistBadge>
              )}
              <p>We'll email you as soon as your account is activated.</p>
              <FormFooter>
                Already activated?
                <Link to="/login">Sign in</Link>
              </FormFooter>
            </WaitlistConfirmation>
          ) : (
            <>
              <FormHeader>
                <h2>Create your account</h2>
                <p>Start planning smarter today</p>
              </FormHeader>

              <GoogleSection>
                <GoogleSignInButton
                  buttonText="signup_with"
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                />
              </GoogleSection>

              <Divider>
                <span>or continue with email</span>
              </Divider>

              {error && (
                <ErrorMessage
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </ErrorMessage>
              )}

              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="name">Full name</Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                    disabled={loading}
                    autoComplete="name"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </FormGroup>

                <PasswordRow>
                  <FormGroup>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="6+ characters"
                      required
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="confirmPassword">Confirm</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      required
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </FormGroup>
                </PasswordRow>

                <SubmitButton
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.01 }}
                  whileTap={{ scale: loading ? 1 : 0.99 }}
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </SubmitButton>
              </Form>

              <FormFooter>
                Already have an account?
                <Link to="/login">Sign in</Link>
              </FormFooter>
            </>
          )}
        </FormContainer>
      </FormPanel>
    </PageWrapper>
  );
};

export default SignupPage;
